'use client';

import {Trash} from 'lucide-react';
import {type ReactNode, useMemo, type ComponentType} from 'react';
import {cn} from '../../utils/cn';

export interface CartSelectionHeaderRenderParams {
  totalCount: number;
  selectedCount: number;
  allSelected: boolean;
}

type ButtonProps = {
  children?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'outline' | 'ghost' | 'destructive';
  [key: string]: any;
};

type CheckboxProps = {
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  children?: ReactNode;
  className?: string;
  [key: string]: any;
};

export interface CartSelectionHeaderProps {
  totalCount: number;
  selectedCount: number;
  allSelected: boolean;
  onToggleAll: (checked: boolean) => void;
  onClearSelection?: () => void;
  onRemoveSelected?: () => void;
  removeDisabled?: boolean;
  selectAllLabel?: ReactNode;
  clearAllLabel?: ReactNode;
  removeSelectedLabel?: ReactNode;
  counterRenderer?: (params: CartSelectionHeaderRenderParams) => ReactNode;
  className?: string;
  ButtonComponent?: ComponentType<ButtonProps>;
  CheckboxComponent?: ComponentType<CheckboxProps>;
}

const DefaultButton: ComponentType<ButtonProps> = ({children, onClick, disabled, className, size = 'md', variant = 'default', ...props}) => {
  const sizeClasses = {
    sm: 'h-8 px-3 text-xs',
    md: 'h-9 px-4',
    lg: 'h-10 px-6',
  };
  const variantClasses = {
    default: 'bg-primary text-primary-foreground hover:bg-primary/90',
    outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
    ghost: 'hover:bg-accent hover:text-accent-foreground',
    destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
        sizeClasses[size],
        variantClasses[variant],
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
};

const DefaultCheckbox: ComponentType<CheckboxProps> = ({checked, onCheckedChange, children, className, ...props}) => {
  const checkboxId = useMemo(() => Math.random().toString(36), []);
  return (
    <label htmlFor={checkboxId} className="inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        id={checkboxId}
        checked={checked}
        onChange={(e) => onCheckedChange?.(e.target.checked)}
        className="sr-only peer"
        {...props}
      />
      <div
        className={cn(
          'grid place-content-center h-4 w-4 shrink-0 rounded-sm border border-input bg-background shadow focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
          checked && 'bg-primary text-primary-foreground',
          className
        )}
        aria-hidden="true"
      >
        {checked && (
          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        )}
      </div>
      {children && <span className="ml-2 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">{children}</span>}
    </label>
  );
};

const defaultCounterRenderer = ({selectedCount, totalCount, allSelected}: CartSelectionHeaderRenderParams) => {
  if (totalCount === 0) {
    return 'Нет товаров';
  }
  if (allSelected) {
    return 'Выбраны все товары';
  }
  return `Выбрано ${selectedCount} из ${totalCount}`;
};

export const CartSelectionHeader = ({
  totalCount,
  selectedCount,
  allSelected,
  onToggleAll,
  onClearSelection,
  onRemoveSelected,
  removeDisabled,
  selectAllLabel = 'Выбрать все',
  clearAllLabel = 'Отменить все',
  removeSelectedLabel = 'Удалить выбранные',
  counterRenderer = defaultCounterRenderer,
  className,
  ButtonComponent = DefaultButton,
  CheckboxComponent = DefaultCheckbox,
}: CartSelectionHeaderProps) => {
  const counter = useMemo(
    () => counterRenderer({totalCount, selectedCount, allSelected}),
    [counterRenderer, totalCount, selectedCount, allSelected],
  );
  const clearDisabled = totalCount === 0 || selectedCount === 0;

  return (
    <div className={cn('flex flex-wrap items-center justify-between gap-3', className)}>
      <div className="flex flex-wrap items-center gap-3">
        <CheckboxComponent checked={allSelected} onCheckedChange={onToggleAll}>
          {selectAllLabel}
        </CheckboxComponent>
        {onClearSelection && (
          <ButtonComponent
            size="sm"
            variant="ghost"
            onClick={onClearSelection}
            disabled={clearDisabled}
          >
            {clearAllLabel}
          </ButtonComponent>
        )}
        <span className="text-sm text-gray-500">{counter}</span>
      </div>
      {onRemoveSelected && (
        <ButtonComponent
          size="sm"
          variant="destructive"
          onClick={onRemoveSelected}
          disabled={removeDisabled || selectedCount === 0}
          className="flex items-center gap-2"
        >
          <Trash className="h-4 w-4" />
          <span className="hidden sm:inline">{removeSelectedLabel}</span>
        </ButtonComponent>
      )}
    </div>
  );
};
